/* eslint-disable jsx-a11y/alt-text */
import {
  EyeOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import {
  Button,
  Collapse,
  CollapseProps,
  Switch,
  Typography,
} from 'antd';
import { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import classes from '../FAQ.module.scss';
import cn from 'classnames';
import Assets from 'assets/icons';
import { useSearchParams } from 'react-router';
import React from 'react';
import TableRU from './tables/tableRU';
import TableEN from './tables/tableEN';
const { Title } = Typography;

const initialItems = [
  [1, 2, 3, 4, 5, 6, '6_1', '6_2', 7, 8, 9],
  [1, 2, 3, 4, 5, 6, 7],
  [1, 2, 3, '3_1', '3_2', '3_3', '3_4', '3_5', '3_6', '3_7', 4, '4_1', '4_2', '4_3'],
  [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  [
    1, 2, '2_1', '2_2', '2_3', '2_4', '2_5', 3, 4, 5, 6, 7, 8, 9, '9_1', '9_2',
    '9_3', '9_4', '9_5', '9_6', '9_7', '9_8', '10', '10_1', '11', '11_1', '11_2',
    '11_3', '11_4', '11_5', '12', '12_1', '12_2', '12_3', '12_4', '12_5',
  ],
  [1, 2, 3, '3_1', '3_2', '3_3', '3_4'],
  [1, 2, 3, 4, 5, 6, 7],
  [1, 2, 3, 4],
  [1, 2, 3],
  [1, '1_1', '1_2', '1_3'],
  [1, 2, 3, 4, 5],
];

const FAQ_COMPONENTS = {
  // eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid
  a: <a />,
  settingsIcon: <Button
    className={classes.settingButton}
    style={{cursor: 'default', pointerEvents: 'none', margin: '0 2px'}}
    icon={<Assets.SettingsIcon/>}
    type='text'
    size='small'
  />,
  shareIcon: <ShareAltOutlined className={classes.icon} />,
  chatIcon: <Button
    className={classes.editIcon}
    icon={<Assets.ChatIcon />}
    type='text'
  />,
  eyeIcon: <EyeOutlined className={cn(classes.icon, classes.eyeIcon)} />,
  filesBtn: (
    <Button
      size='middle'
      shape='default'
      style={{cursor: 'default', pointerEvents: 'none', margin: '0 5px 10px 5px'}}
      type='primary'
      color='primary'
    />
  ),
  addRoom: (
    <Button
      className={classes.addRoom}
      icon={<Assets.RoomsAddIcon />}
      shape='circle'
      type='primary'
    />
  ),
  plusBtn: (
    <Button
      className={classes.plusButton}
      icon={<Assets.PlusIcon />}
      shape='circle'
      size='small'
      type='primary'
    />
  ),
  editBtn: (
    <Button
      className={classes.editIcon}
      icon={<Assets.EditIcon />}
      type='text'
    />
  ),
  switch: <Switch checked={false} disabled />,
  tableRU: <TableRU />,
  tableEN: <TableEN />,
  img: <img />,
  'img.small': <img className={classes.small} />,
  b: <b />,
  i: <i />,
  p: <p />,
  u: <u />,
  ul: <ul />,
  'ul.circle': <ul className={classes.circle} />,
  'ul.square': <ul className={classes.square} />,
  ol: <ol className={classes.ol} />,
  li: <li />,
  br: <br />,
  div: <div />,
  'div.center': <div className={classes.center} />,
};

export default function FAQTab({ t }: { t: TFunction }) {
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();

  const section = searchParams.get('section');
  const actualLanguage = i18n.language === 'en' ? '' : 'ru/';

  const buildName = window.REACT_APP_BUILD_NAME !== 'REPLACE_REACT_APP_BUILD_NAME' ? window.REACT_APP_BUILD_NAME : process.env.REACT_APP_BUILD_NAME;
  const appName = buildName === 'athena' ? 'Athena' : 'TazaDeal';  
  const support = buildName === 'athena' ? 'support@locus-soft.com' : 'support@taza-digital.com';
  const webSite = buildName === 'athena' ? 'locus-soft' : 'tazadeal';
  const tariffs = buildName === 'athena' ? "#plans" : `${actualLanguage}plans`;
  const documents = buildName === 'athena' ? "documents" : `${actualLanguage}documents`

  React.useEffect(() => {
    section && document?.getElementById(String(section))?.scrollIntoView();
  }, [section])
  
  const modifiedItems: CollapseProps['items'][] = initialItems.map(
    (keys, sectionIdx) => {
      const sectionKey = `FAQ.FAQ.${sectionIdx + 1}`;
      if (!i18n.exists(sectionKey)) return [];

      const items = keys
        .map((item) => {
          if (!i18n.exists(`FAQ.FAQ.${sectionIdx + 1}_${item}.question`)) return null;
          let i18nKey: string = `FAQ.${sectionIdx + 1}_${item}.answer`;
          if (sectionIdx === 0 && (item === 7 || item === 8)) {
            i18nKey = `FAQ.${sectionIdx + 1}_${item}.answer.${buildName}`;
          };

          return {
            key: `${sectionIdx + 1}.${item}`,
            label: (
              <Title level={5}>
                {t(`FAQ.${sectionIdx + 1}_${item}.question`, { appName })}
              </Title>
            ),
            id: `${sectionIdx + 1}.${item}`,
            children: (
              <div>
                <Trans
                  i18nKey={i18nKey}
                  t={t}
                  components={FAQ_COMPONENTS}
                  values={{ appName, support, webSite, tariffs, documents, uploadText: t('Documents.management.addFiles') }}
                  tOptions={{ ampersand: '/', interpolation: { escapeValue: false } }}
                />
              </div>
            ),
          };
        })
        .filter(Boolean) as CollapseProps['items'];

      return items;
    }
  );

  return (
    <div>
      {modifiedItems.map((items, idx) =>
        items!.length > 0 ? (
          <div key={`FAQ-${idx}`} className={classes.section}>
            <Title level={2}>{t(`FAQ.${idx + 1}`)}</Title>
            <Collapse
              items={items} 
              defaultActiveKey={[section || '1.1']}
            />
          </div>
        ) : null
      )}
    </div>
  );
}
